import type { EntityID, Thread } from '@wovin/core'
import type { DatalogQueryPattern } from '@wovin/core/applog'
import type { KidRenderingPlugin, PluginSettingsProvider, BlockBulletRenderer, SingleBlockRenderer } from './types'
import type { RootProvider } from './seam/contract'

// ────────────────────────────────────────────────────────────────────
// KidRenderingPlugin registry
// ────────────────────────────────────────────────────────────────────

const kidRenderingPlugins = new Map<string, KidRenderingPlugin>()
/** Plugins that also have a matchesEntity hook (cached for dispatch) */
const entityPluginList: Array<{ plugin: KidRenderingPlugin; matches: (thread: Thread, id: EntityID) => boolean }> = []

export function registerKidRenderingPlugin(plugin: KidRenderingPlugin): void {
	if (kidRenderingPlugins.has(plugin.type)) {
		console.warn(`[plugin-registry] Overwriting KidRenderingPlugin for type "${plugin.type}"`)
	}
	kidRenderingPlugins.set(plugin.type, plugin)

	if (plugin.matchesEntity) {
		const idx = entityPluginList.findIndex(e => e.plugin.type === plugin.type)
		if (idx >= 0) entityPluginList.splice(idx, 1)
		entityPluginList.push({ plugin, matches: plugin.matchesEntity })
	}
}

export function getKidRenderingPlugin(blockType: string): KidRenderingPlugin | undefined {
	return kidRenderingPlugins.get(blockType)
}

export function getAllKidRenderingPlugins(): KidRenderingPlugin[] {
	return [...kidRenderingPlugins.values()]
}

export function findPluginForEntity(thread: Thread, entityID: EntityID): KidRenderingPlugin | undefined {
	return entityPluginList.find(e => e.matches(thread, entityID))?.plugin
}

/**
 * Collect virtual root discovery patterns from all registered plugins.
 * useRoots() subscribes to these patterns alongside block/content for
 * reactive virtual root injection.
 */
export function getVirtualRootDiscoveryPatterns(): DatalogQueryPattern[] {
	const patterns: DatalogQueryPattern[] = []
	for (const plugin of kidRenderingPlugins.values()) {
		if (plugin.getVirtualRootDiscoveryPatterns) {
			patterns.push(...plugin.getVirtualRootDiscoveryPatterns)
		}
	}
	return patterns
}

// ────────────────────────────────────────────────────────────────────
// BlockBulletRenderer registry
// ────────────────────────────────────────────────────────────────────

const blockBulletRenderers: BlockBulletRenderer[] = []

export function registerBlockBulletRenderer(renderer: BlockBulletRenderer): void {
	blockBulletRenderers.push(renderer)
	// Keep sorted by priority (highest first) — deterministic iteration
	blockBulletRenderers.sort((a, b) => b.priority - a.priority)
}

/** Returns all registered BlockBulletRenderers, sorted by priority descending. */
export function getBlockBulletRenderers(): readonly BlockBulletRenderer[] {
	return blockBulletRenderers
}

// ────────────────────────────────────────────────────────────────────
// SingleBlockRenderer registry
// ────────────────────────────────────────────────────────────────────

const singleBlockRenderers = new Map<string, SingleBlockRenderer>()

export function registerSingleBlockRenderer(renderer: SingleBlockRenderer): void {
	if (singleBlockRenderers.has(renderer.name)) {
		console.warn(`[plugin-registry] Overwriting SingleBlockRenderer "${renderer.name}"`)
	}
	singleBlockRenderers.set(renderer.name, renderer)
}

export function getSingleBlockRenderer(name: string): SingleBlockRenderer | undefined {
	return singleBlockRenderers.get(name)
}

// ────────────────────────────────────────────────────────────────────
// RootProvider registry (seam v1.1 — see seam/contract.ts)
// ────────────────────────────────────────────────────────────────────

const rootProviders: RootProvider[] = []

export function registerRootProvider(provider: RootProvider): void {
	const idx = rootProviders.findIndex(p => p.name === provider.name)
	if (idx >= 0) {
		console.warn(`[plugin-registry] Overwriting RootProvider "${provider.name}"`)
		rootProviders.splice(idx, 1)
	}
	rootProviders.push(provider)
}

export function getRootProviders(): readonly RootProvider[] {
	return rootProviders
}

// ────────────────────────────────────────────────────────────────────
// Editor-extension plugins (consumed by BlockContent editor creation)
// ────────────────────────────────────────────────────────────────────

export interface ActiveEditorPlugin {
	name?: string
	tiptapExtensions: any[] // tiptap Extension instances (typed loosely to avoid a tiptap dep here)
}
/** Loaded plugins contributing tiptap extensions (moved here from init.ts) */
export const activePlugins: ActiveEditorPlugin[] = []

// ────────────────────────────────────────────────────────────────────
// Extra hidden-prefix providers (beyond KidRenderingPlugins)
// ────────────────────────────────────────────────────────────────────

const hiddenPrefixProviders: Array<() => string[]> = []
export function registerHiddenExtraPropPrefixes(provider: () => string[]): void {
	hiddenPrefixProviders.push(provider)
}

// ────────────────────────────────────────────────────────────────────
// Plugin settings registry
// ────────────────────────────────────────────────────────────────────

/** Collect settings from all registered plugins that provide getSettings() */
export function collectPluginSettings(): PluginSettingsProvider[] {
	return getAllKidRenderingPlugins()
		.filter(p => typeof p.getSettings === 'function')
		.map(p => p.getSettings!())
}

/**
 * Collect extra-props attribute prefixes that should be hidden
 * (registered by plugins like transcriptions to suppress raw applogs).
 */
export function collectHiddenExtraPropPrefixes(): string[] {
	const prefixes: string[] = []
	for (const plugin of kidRenderingPlugins.values()) {
		const result = plugin.getHiddenExtraPropPrefixes?.()
		if (result) {
			prefixes.push(...result)
		}
	}
	for (const provider of hiddenPrefixProviders) {
		prefixes.push(...provider())
	}
	return prefixes
}

// ────────────────────────────────────────────────────────────────────
// Bulk registration helper (used by init.ts)
// ────────────────────────────────────────────────────────────────────

export interface BuiltinPluginBundle {
	kidRenderingPlugins?: KidRenderingPlugin[]
	blockBulletRenderers?: BlockBulletRenderer[]
	singleBlockRenderers?: SingleBlockRenderer[]
}

export function registerBuiltinPlugins(bundle: BuiltinPluginBundle): void {
	bundle.kidRenderingPlugins?.forEach(registerKidRenderingPlugin)
	bundle.blockBulletRenderers?.forEach(registerBlockBulletRenderer)
	bundle.singleBlockRenderers?.forEach(registerSingleBlockRenderer)
}
